import { useEffect, useMemo, useState, type FormEvent } from "react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { useLegalPages, useUpdateLegalPage } from "@/features/legal-pages/hooks";
import type { LegalPage, LegalPageInput, LegalStatus } from "@/features/legal-pages/types";
import { cn } from "@/lib/cn";

const legalOrder = ["terms", "privacy", "terms-of-use", "disclaimer"];

export function LegalPagesPage() {
  const pages = useLegalPages();
  const updatePage = useUpdateLegalPage();
  const sortedPages = useMemo(
    () => [...(pages.data ?? [])].sort((a, b) => legalOrder.indexOf(a.slug) - legalOrder.indexOf(b.slug)),
    [pages.data],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedPage = sortedPages.find((page) => page.id === selectedId) ?? sortedPages[0] ?? null;
  const [form, setForm] = useState<LegalPageInput>({ title: "", slug: "", content: "", status: "DRAFT" });
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!selectedPage) return;
    setSelectedId((current) => current ?? selectedPage.id);
    setForm({
      title: selectedPage.title,
      slug: selectedPage.slug,
      content: selectedPage.content,
      status: selectedPage.status,
    });
    setMessage(null);
  }, [selectedPage]);

  function handleSelect(page: LegalPage) {
    setSelectedId(page.id);
    setForm({ title: page.title, slug: page.slug, content: page.content, status: page.status });
    setMessage(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    if (!selectedPage) return;
    updatePage.mutate(
      { page: selectedPage, input: form },
      {
        onSuccess: (page) => {
          setForm({ title: page.title, slug: page.slug, content: page.content, status: page.status });
          setMessage({ tone: "success", text: "Legal page saved." });
        },
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to save legal page." }),
      },
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Legal Pages"
        description="Edit the default legal pages. Missing defaults are created automatically by the API."
      />
      {pages.isLoading ? (
        <div className="rounded-md border border-cyan-300/15 bg-black/25 p-6 text-sm text-content-secondary">Loading legal pages...</div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[17rem_1fr]">
          <aside className="space-y-2">
            {sortedPages.map((page) => (
              <button
                key={page.id}
                type="button"
                className={cn(
                  "w-full rounded-md border px-3 py-3 text-left transition",
                  selectedPage?.id === page.id
                    ? "border-cyan-300/60 bg-cyan-300/10 text-white"
                    : "border-cyan-300/15 bg-black/25 text-content-secondary hover:border-cyan-300/40 hover:text-white",
                )}
                onClick={() => handleSelect(page)}
              >
                <span className="block font-medium">{page.title}</span>
                <span className="mt-1 block text-xs text-content-muted">/{page.slug}</span>
              </button>
            ))}
          </aside>
          {selectedPage && (
            <form className="space-y-5 rounded-md border border-cyan-300/15 bg-black/25 p-4" onSubmit={handleSubmit}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-content-muted">Updated</p>
                  <p className="text-sm text-content-secondary">{new Date(selectedPage.updatedAt).toLocaleString()}</p>
                </div>
                <StatusBadge tone={form.status === "PUBLISHED" ? "active" : form.status === "ARCHIVED" ? "warning" : "muted"}>
                  {form.status === "PUBLISHED" ? "Published" : form.status}
                </StatusBadge>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <TextField label="Title" value={form.title} onChange={(value) => setForm((current) => ({ ...current, title: value }))} required />
                <TextField label="Slug" value={form.slug} onChange={(value) => setForm((current) => ({ ...current, slug: value }))} required />
              </div>
              <label className="block">
                <span className="text-sm text-content-secondary">Status</span>
                <select
                  className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
                  value={form.status}
                  onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as LegalStatus }))}
                >
                  <option value="DRAFT">Draft</option>
                  <option value="PUBLISHED">Published</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </label>
              <label className="block">
                <span className="text-sm text-content-secondary">Markdown Content</span>
                <textarea
                  className="mt-2 min-h-[28rem] w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-3 font-mono text-sm text-white outline-none transition focus:border-cyan-300"
                  value={form.content}
                  onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
                  required
                />
              </label>
              {message && (
                <p
                  className={
                    message.tone === "success"
                      ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100"
                      : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"
                  }
                >
                  {message.text}
                </p>
              )}
              <div className="flex justify-end">
                <Button type="submit" disabled={updatePage.isPending}>
                  {updatePage.isPending ? "Saving..." : "Save Legal Page"}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
    </label>
  );
}
